import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const categoryArticles = useMemo(
    () => (slug ? getArticlesByCategory(slug) : []),
    [slug]
  );

  const handleCategoryChange = useCallback(
    (newSlug: string) => {
      navigate(`/category/${newSlug}`);
      window.scrollTo({ top: 0, behavior: 'instant' });
    },
    [navigate]
  );

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
              分类未找到
            </h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 爱妻艺找回`,
    description: currentCategory.description,
    url: `https://aiqiyi-zhaihui.com/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://aiqiyi-zhaihui.com/article/${article.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name} - 爱妻艺找回 | ${currentCategory.description.slice(0, 40)}`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},爱妻艺找回,婚姻修复,情感挽回,${currentCategory.name}方法`}
        canonicalUrl={`https://aiqiyi-zhaihui.com/category/${currentCategory.slug}`}
        schema={categorySchema}
      />
      <Navbar />

      <main className="pt-20">
        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="mt-4">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    {currentCategory.name}
                  </h1>
                  <p className="text-foreground-600 leading-relaxed max-w-2xl">
                    {currentCategory.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all cursor-pointer ${
                      cat.slug === currentCategory.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200/70 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {categoryArticles.length > 0 ? (
              <>
                <p className="text-sm text-foreground-500 mb-6">
                  共 {categoryArticles.length} 篇文章
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}